<template>
	<div id="complete-asset-setup">
		<div class="container">
			<div class="md:tw-flex md:tw-justify-between md:tw-items-center tw-mb-8">
				<div class="tw-flex tw-items-center">
					<BackBtn class="tw-hidden sm:tw-block tw-mr-6" />
					<h3 class="tw-font-bold tw-text-xl">Token Details</h3>
				</div>
			</div>
			<form @submit.prevent="uploadImage">
				<div class="tw-bg-white tw-rounded tw-py-8 lg:tw-py-20 tw-px-4">
					<div class="card md:tw-grid md:tw-grid-cols-2 tw-gap-8 tw-mx-auto">
						<div class="md:tw-h-- md:tw-ml-">
							<label for="token image" class="tw-text-sm">Token image</label>
							<DragNDrop
								accept="image/png,image/jpeg,image/jpg,.pdf"
								:currentValue="requestTokenPayload?.image"
								:startUpload="uploadTokenImage"
								@reupload="uploadTokenImage = false"
								@upload-complete="submitRequest"
								@uploadError="uploadImageError"
							>
								<p
									class="tw-text-sm tw-text-secondary tw-w-full tw-text-center tw-mb-"
								>
									Drag and drop file
								</p>
							</DragNDrop>
						</div>
						<div class="tw-mt-6 md:tw-mt-0">
							<TextInput
								placeHolder="Token Name"
								label="Token Name"
								type="text"
								fieldType="text"
								name="token_name"
								id="token_name"
								:showLabel="true"
								:text="requestTokenPayload.name"
								@set="setTokenName"
								@valid="tokenNameValid"
							/>
							<TextInput
								class="tw-mt-6 md:tw-mt-6"
								placeHolder="Token Symbol"
								label="Token Symbol"
								type="text"
								fieldType="text"
								name="token_symbol"
								id="token_symbol"
								:showLabel="true"
								:text="requestTokenPayload.symbol"
								@set="setTokenSymbol"
								@valid="tokenSymbolValid"
							/>
							<label
								class="tw-w-full tw-mt-4 tw-block tw-text-sm"
								for="investmentType"
								>Investment Type</label
							>
							<select
								class="tw-w-full tw-px-4 tw-py-4 tw-rounded"
								v-model="requestTokenPayload.investment_type"
								name="investmentType"
								id="investmentType"
							>
								<option
									v-for="(type, index) in investmentTypes"
									:key="index"
									:value="type"
								>
									{{ type }}
								</option>
							</select>
							<template v-if="requestTokenPayload.investment_type !== 'SAFE'">
								<label
									class="tw-w-full tw-mt-4 tw-block tw-text-sm"
									for="payout_frequency"
									>Payout Frequency</label
								>
								<select
									class="tw-w-full tw-px-4 tw-py-4 tw-rounded"
									v-model="requestTokenPayload.payout_frequency"
									name="payout_frequency"
									id="payout_frequency"
								>
									<option
										v-for="(frequency, index) in payoutFrequencies"
										:key="index"
										:value="frequency.value"
									>
										{{ frequency.name }}
									</option>
								</select>
							</template>
						</div>
						<div class="tw-relative">
							<div class="">
								<label
									class="tw-w-full tw-mt-4 tw-block tw-text-sm"
									for="investmentType"
									>Deal Country</label
								>
								<select
									class="tw-w-full tw-px-4 tw-py-4 tw-rounded"
									v-model="requestTokenPayload.country"
									name="country"
									id="country"
								>
									<option
										v-for="(country, index) in countryRegionState"
										:key="index"
										:value="country.countryName"
									>
										{{ country.countryName }}
									</option>
								</select>
							</div>
							<div class="">
								<label
									class="tw-w-full tw-mt-4 tw-block tw-text-sm"
									for="investmentType"
									>Deal Currency</label
								>
								<select
									class="tw-w-full tw-px-4 tw-py-4 tw-rounded"
									v-model="requestTokenPayload.currency"
									name="currency"
									id="currency"
								>
									<option
										v-for="(currency, index) in currencies"
										:key="index"
										:value="currency.value"
									>
										{{ currency.name }}
									</option>
								</select>
							</div>
						</div>
						<div class="tw-relative">
							<NumberInput
								v-if="
									requestTokenPayload.investment_type === 'Debt' ||
									requestTokenPayload.investment_type === 'Fixed Interest'
								"
								class="tw-mt-6"
								placeHolder="Interest Rate"
								label="Interest Rate (%)"
								type="text"
								name="interest"
								id="interest"
								:showLabel="true"
								:number="requestTokenPayload.interest"
								@set="setInterestRate"
								@valid="interestValid"
							/>
							<NumberInput
								v-else
								class="tw-mt-6"
								placeHolder="Valuation"
								:label="`Valuation (${requestTokenPayload.currency})`"
								type="text"
								fieldType="valuation"
								name="valuation"
								id="valuation"
								:showLabel="true"
								:valuation="requestTokenPayload.valuation"
								@set="setValuation"
								@valid="valuationValid"
							/>
							<NumberInput
								v-if="requestTokenPayload.investment_type === 'SAFE'"
								class="tw-mt-6"
								placeHolder="Discount"
								label="Discount (%)"
								type="number"
								name="discount"
								id="discount"
								minNumber="1"
								:showLabel="true"
								:number="requestTokenPayload.discount"
								@set="setDiscount"
								@valid="discountValid"
							/>
						</div>
						<div
							class="tw-relative"
							v-if="
								requestTokenPayload.investment_type === 'Debt' ||
								requestTokenPayload.investment_type === 'Fixed Interest'
							"
						>
							<NumberInput
								class="tw-mt-6 md:tw-mt-"
								placeHolder="Investment Period"
								label="Investment Period (months)"
								type="text"
								name="tenor"
								id="tenor"
								:number="requestTokenPayload.tenor"
								:showLabel="true"
								@set="setDiscount"
								@valid="tenorValid"
							/>
						</div>
						<div class="tw-relative">
							<NumberInput
								class="tw-mt-6 md:tw-mt-"
								placeHolder="Raise Amount"
								:label="`Raise Amount (${requestTokenPayload.currency})`"
								type="text"
								fieldType="raiseAmount"
								name="raise_amount"
								id="raise_amount"
								:showLabel="true"
								:raiseAmount="requestTokenPayload.raise_amount"
								:maxNumber="requestTokenPayload.valuation"
								:disabled="!requestTokenPayload.valuation"
								@set="setRaiseAmount"
								@valid="raiseAmountValid"
							/>
						</div>
						<div
							class="tw-flex tw-items-start tw-bg-orange-light tw-rounded tw-text-dark tw-py-8 tw-px-4 tw-mt-6 md:tw-mt-0"
						>
							<img
								class="tw-mt-0.5"
								src="@/assets/img/orange-noti.svg"
								alt="notification"
							/>
							<div class="tw-ml-4">
								<p class="tw-text-sm">
									Your token price will be set at
									{{
										formatAmountToDollar(
											geTokenPrice,
											2,
											requestTokenPayload.currency
										)
									}}
								</p>
								<p
									class="tw-w-24 tw-font-semibold tw-underline tw-cursor-pointer"
									@click="showLearnMore = !showLearnMore"
								>
									Learn More
								</p>
							</div>
						</div>
						<div></div>
						<div></div>
						<div></div>
					</div>
				</div>
				<div class="tw-flex tw-justify-end">
					<Btn
						class="md:tw-w-52 tw-mt-8"
						:loading="tokenRequestLoading"
						title="Submit"
					/>
				</div>
			</form>
		</div>
		<InfoModal v-if="showLearnMore" @close="showLearnMore = !showLearnMore">
			<template v-slot:content>
				<div class="tw-px-4 tw-py-8">
					<div class="tw-flex tw-justify-center">
						<img
							class="tw-w-20"
							src="@/assets/img/orange-info.svg"
							alt="info"
						/>
					</div>
					<div class="tw-text-center">
						<h3 class="tw-text-xl tw-font-semibold tw-mt-4">
							About Equity Price
						</h3>
						<p class="tw-text-gray tw-mt-4">
							Tokenizing this equity requires that we set the price at this
							amount so it is not only accessible for all, it is also to manage
							transactions and transfers" The amount you are looking to raise is
							divided by
							{{
								formatAmountToDollar(
									geTokenPrice,
									2,
									requestTokenPayload.currency
								)
							}}
							to give you the total number of units you have on sale
						</p>
					</div>
					<Btn
						class="tw-mt-8"
						title="I Understand"
						@click="showLearnMore = !showLearnMore"
					/>
				</div>
			</template>
		</InfoModal>
		<InfoModal
			v-if="showSuccessModal"
			@close="$router.push({ name: 'PublicRaise' })"
		>
			<template v-slot:content>
				<div class="tw-py-8">
					<div class="tw-flex tw-justify-center">
						<img
							class="tw-w-20"
							src="@/assets/img/success-mark-large.svg"
							alt="info"
						/>
					</div>
					<div class="tw-text-center">
						<h3 class="tw-text-xl tw-font-semibold tw-mt-4">
							Your request for listing has been submitted
						</h3>
						<p class="tw-text-gray tw-mt-4">
							You will be notified once we’re done vetting your request. This
							typically takes less than 48 hours.
						</p>
					</div>
					<Btn
						class="tw-mt-8"
						title="Done"
						@click="$router.push({ name: 'PublicRaise' })"
					/>
				</div>
			</template>
		</InfoModal>
	</div>
</template>

<script>
import BackBtn from "@/components/general/BackBtn.vue";
import Btn from "@/components/general/BtnComponent.vue";
import DragNDrop from "@/components/general/DragNDrop.vue";
import NumberInput from "@/components/general/NumberInput.vue";
import InfoModal from "@/components/general/SmallModal.vue";
import TextInput from "@/components/general/TextInput.vue";
import { formatAmountToDollar } from "@/utils/helpers";
import countryRegionState from "@/utils/js/countryRegionData";
import { mapActions, mapState } from "vuex";

export default {
	name: "CreatePublicRaise",

	components: {
		TextInput,
		NumberInput,
		DragNDrop,
		InfoModal,
		Btn,
		BackBtn,
	},

	data() {
		return {
			tokenRequestLoading: false,
			countryRegionState,
			uploadTokenImage: false,
			assetInfoLoading: false,
			showLearnMore: false,
			showSuccessModal: false,
			validInput: {
				name: false,
				symbol: false,
				raise_amount: false,
				valuation: false,
				investment_type: true,
				interest: false,
				tenor: false,
				discount: false,
				risk: true,
				country: false,
				currency: false,
			},
			requestTokenPayload: {
				// asset_id: "",
				image: "",
				name: "",
				raise_amount: "",
				symbol: "",
				valuation: "",
				interest: "",
				tenor: "",
				investment_type: "SAFE",
				discount: "",
				currency: "USD",
				risk: "",
				country: "",
				payout_frequency: "",
			},
			investmentTypes: ["Equity", "Debt", "SAFE", "Fixed Interest"],
			payoutFrequencies: [
				{ name: "Daily", value: "Daily" },
				{
					name: "Weekly",
					value: "Weekly",
				},
				{
					name: "Monthly",
					value: "Monthly",
				},
				{
					name: "Quarterly",
					value: "Quarterly",
				},
				{
					name: "Annually",
					value: "Annually",
				},
			],
			currencies: [
				{
					name: "United States Dollars",
					value: "USD",
				},
				{
					name: "Nigerian Naira",
					value: "NGN",
				},
				{
					name: "Kenyan Shillings",
					value: "KES",
				},
			],
			suggestedSymbolName: "",
		};
	},

	computed: {
		...mapState({
			orgDetails: (state) => state.organisationModule.organisationDetails,
			assetDetails: (state) => state.assetModule.assetDetails,
			tokenRequest: (state) => state.assetModule.tokenRequest,
		}),
		geTokenPrice() {
			let currency = this.requestTokenPayload.currency;
			let value = null;
			switch (currency) {
				case "USD":
					value = 10;
					break;
				case "NGN":
					value = 10000;
					break;
				case "KES":
					value = 100;
					break;
			}
			return value;
		},
	},

	created() {
		this.fetchAsset();
	},

	methods: {
		...mapActions("assetModule", [
			"getAsset",
			"createTokenRequest",
			"getTokenRequest",
			"updateTokenRequest",
		]),
		...mapActions(["showToast"]),
		formatAmountToDollar,
		async submitRequest(url) {
			const payload = {
				asset_id: this.orgDetails?.asset || this.$route.query.asset,
				image: url,
				name: this.requestTokenPayload.name,
				raise_amount: Number(this.requestTokenPayload.raise_amount),
				symbol: this.requestTokenPayload.symbol,
				type: "Public",
				investment_type: this.requestTokenPayload.investment_type,
				interest: this.requestTokenPayload.interest,
				tenor: Number(this.requestTokenPayload.tenor),
				discount: Number(this.requestTokenPayload.discount),
				valuation: Number(this.requestTokenPayload.valuation),
				currency: this.requestTokenPayload.currency,
				risk: this.requestTokenPayload.risk,
				country: this.requestTokenPayload.country,
				payout_frequency: this.requestTokenPayload.payout_frequency,
			};
			if (
				this.requestTokenPayload.investment_type !== "Debt" &&
				this.requestTokenPayload.investment_type !== "Fixed Interest"
			) {
				delete payload.interest;
				delete payload.tenor;
			}

			if (
				this.requestTokenPayload.investment_type === "Equity" ||
				this.requestTokenPayload.investment_type === "SAFE"
			) {
				payload.risk = "High";
				this.requestTokenPayload.risk = "High";
			} else {
				payload.risk = "Medium";
				this.requestTokenPayload.risk = "Medium";
			}

			if (this.requestTokenPayload.investment_type !== "SAFE") {
				delete payload.discount;
			}
			if (this.tokenRequest) {
				delete payload.asset_id;
				const response = await this.updateTokenRequest({
					id: this.tokenRequest._id,
					payload,
				});
				if (response) this.showSuccessModal = true;
			} else {
				const response = await this.createTokenRequest(payload);
				if (response) this.showSuccessModal = true;
			}
			this.uploadTokenImage = false;
			this.tokenRequestLoading = false;
		},

		setTokenName(text) {
			this.requestTokenPayload.name = text;
		},

		setTokenSymbol(text) {
			this.requestTokenPayload.symbol = text.toUpperCase();
		},

		setRaiseAmount(number) {
			this.requestTokenPayload.raise_amount = number;
		},

		setValuation(number) {
			this.requestTokenPayload.valuation = number;
		},
		setInterestRate(number) {
			this.requestTokenPayload.interest = number;
		},
		setTenor(number) {
			this.requestTokenPayload.tenor = number;
		},
		setDiscount(number) {
			this.requestTokenPayload.discount = number;
		},

		tokenNameValid(valid) {
			this.validInput.name = valid;
		},

		tokenSymbolValid(valid) {
			this.validInput.symbol = valid;
		},

		raiseAmountValid(valid) {
			this.validInput.raise_amount = valid;
		},

		valuationValid(valid) {
			this.validInput.valuation = valid;
		},
		interestValid(valid) {
			this.validInput.interest = valid;
		},
		tenorValid(valid) {
			this.validInput.tenor = valid;
		},
		discountValid(valid) {
			this.validInput.discount = valid;
		},

		async fetchAsset() {
			if (!this.orgDetails.asset && !this.$route.query.asset) return;
			// if (!this.$route.query.asset) return;
			let payload;
			this.$route.query.asset
				? (payload = this.$route.query.asset)
				: (payload = this.orgDetails.asset);
			await this.getAsset(payload);
			await this.getTokenRequest();
			this.updateTokenInfo();
		},

		updateTokenInfo() {
			if (this.tokenRequest) {
				this.requestTokenPayload.name = this.tokenRequest?.name;
				this.requestTokenPayload.image = this.tokenRequest?.image;
				this.requestTokenPayload.symbol = this.tokenRequest?.symbol;
				this.requestTokenPayload.investment_type =
					this.tokenRequest?.investment_type;
				this.requestTokenPayload.country = this.tokenRequest?.country;
				this.requestTokenPayload.currency = this.tokenRequest?.currency;
				this.requestTokenPayload.risk = this.tokenRequest?.risk;
				if (this.tokenRequest.investment_type === "SAFE") {
					this.requestTokenPayload.discount = String(
						this.tokenRequest?.discount
					);
				}
				if (this.tokenRequest.payout_frequency) {
					this.requestTokenPayload.payout_frequency =
						this.tokenRequest?.payout_frequency;
				}
				if (
					this.tokenRequest.investment_type === "Debt" ||
					this.tokenRequest.investment_type === "Fixed Interest"
				) {
					this.requestTokenPayload.interest = String(
						this.tokenRequest?.interest
					);
					this.requestTokenPayload.tenor = String(this.tokenRequest?.tenor);
				}
				// this.requestTokenPayload.asset_id = this.orgDetails?.asset;
				if (!this.tokenRequest?.valuation) return;
				this.requestTokenPayload.valuation = String(
					this.tokenRequest?.valuation
				);
				this.requestTokenPayload.raise_amount = String(
					this.tokenRequest?.raise_amount
				);
			} else {
				this.requestTokenPayload.name = this.assetDetails?.name;
				this.requestTokenPayload.image = this.assetDetails?.image;
				this.requestTokenPayload.symbol = this.assetDetails?.symbol;
			}
		},

		uploadImageError() {
			this.assetInfoLoading = false;
			this.uploadTokenImage = false;
		},

		uploadImage() {
			if (
				!this.validInput.name ||
				!this.validInput.symbol ||
				!this.validInput.raise_amount ||
				!this.validInput.valuation ||
				!this.validInput.investment_type ||
				!this.validInput.country ||
				!this.validInput.currency ||
				!this.validInput.risk
			) {
				return this.showToast({
					description: "Please fill all input field correctly",
					display: true,
					type: "error",
				});
			}
			this.tokenRequestLoading = true;
			setTimeout(() => {
				this.assetInfoLoading = true;
				this.uploadTokenImage = true;
			}, 500);
		},

		createTokenSymbol() {
			if (this.tokenRequest) return;
			this.suggestedSymbolName = "";
			const tokenName = this.requestTokenPayload.name;
			let suggestedSymbolName = this.suggestedSymbolName;
			const tokenNameItems = tokenName.split(" ");
			tokenNameItems.map((item) => {
				if (suggestedSymbolName.length < 6) {
					suggestedSymbolName += item.charAt(0).toUpperCase();
				}
			});
			this.tokenNameItems = tokenNameItems;
			this.requestTokenPayload.symbol = suggestedSymbolName;
			this.suggestedSymbolName = suggestedSymbolName;
			this.checkSuggestedSymbolLength();
		},

		checkSuggestedSymbolLength() {
			let suggestedSymbolName = this.suggestedSymbolName;
			const tokenNameItems = this.tokenNameItems;
			const suggestedSymbolNameLength = suggestedSymbolName.length.toString();
			const mergedTokenName = tokenNameItems.join("");
			const mergedTokenNameLength = mergedTokenName.length;
			const secondLetter = tokenNameItems[0].charAt(1);
			const midPoint = Math.round(mergedTokenNameLength / 2);
			const middleLetter = mergedTokenName.charAt(midPoint);
			const endPoint = mergedTokenNameLength - 1;
			const lastLetter = mergedTokenName.charAt(endPoint);
			switch (suggestedSymbolNameLength) {
				case "1":
					suggestedSymbolName += secondLetter.toUpperCase();
					break;
				case "2":
					suggestedSymbolName += middleLetter.toUpperCase();
					break;
				case "3":
					suggestedSymbolName += lastLetter.toUpperCase();
					break;
			}
			this.suggestedSymbolName = suggestedSymbolName;
			this.requestTokenPayload.symbol = suggestedSymbolName;
		},
	},

	watch: {
		"requestTokenPayload.name"(newValue, oldValue) {
			if (newValue !== oldValue) this.createTokenSymbol();
		},
		"requestTokenPayload.investment_type"(value) {
			if (value === "Debt" || value === "Fixed Income") {
				this.requestTokenPayload.valuation;
			}
			this.validInput.investment_type = true;
		},
		"requestTokenPayload.raise_amount"(value) {
			if (
				this.requestTokenPayload.investment_type === "Debt" ||
				this.requestTokenPayload.investment_type === "Fixed Income"
			) {
				this.requestTokenPayload.valuation = String(Number(value) + 1);
			}
			this.validInput.investment_type = true;
		},
		"requestTokenPayload.country"(value) {
			this.validInput.country = true;
		},
		"requestTokenPayload.currency"(value) {
			this.validInput.currency = true;
		},
	},
};
</script>

<style lang="scss" scoped>
#complete-asset-setup {
	.container {
		@include fadeIn;

		@media screen and (min-width: 1024px) {
			max-width: 1000px;
			margin-left: auto;
			margin-right: auto;
		}

		.card {
			max-width: 700px;
		}
	}
}
</style>
